/* Routes. Nineteen built screens, and one that is genuinely step 12.

   Every placeholder names the contract that already returns its data, so picking
   one up is a component job rather than a service job. */

import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { AuditScreen } from './components/screens/AuditScreen';
import { BackupDrScreen } from './components/screens/BackupDrScreen';
import { CredentialsScreen } from './components/screens/CredentialsScreen';
import { ApplicationsScreen } from './components/screens/ApplicationsScreen';
import { DashboardScreen } from './components/screens/DashboardScreen';
import { DevicesScreen } from './components/screens/DevicesScreen';
import { DomainsScreen } from './components/screens/DomainsScreen';
import { ExpiriesScreen } from './components/screens/ExpiriesScreen';
import { GroupsScreen } from './components/screens/GroupsScreen';
import { ImportScreen } from './components/screens/ImportScreen';
import { KnowledgeBaseScreen } from './components/screens/KnowledgeBaseScreen';
import { LicencesScreen } from './components/screens/LicencesScreen';
import { ModulesScreen } from './components/screens/ModulesScreen';
import { NotificationsScreen } from './components/screens/NotificationsScreen';
import { JoinerWizard } from './components/screens/JoinerWizard';
import { LocationsScreen } from './components/screens/LocationsScreen';
import { NetworkScreen } from './components/screens/NetworkScreen';
import { LabelsScreen } from './components/screens/LabelsScreen';
import { PackScreen } from './components/screens/PackScreen';
import { InfrastructureScreen } from './components/screens/InfrastructureScreen';
import { PrintersScreen } from './components/screens/PrintersScreen';
import { OnboardingScreen, OffboardingScreen } from './components/screens/OnboardingScreen';
import { ProceduresScreen } from './components/screens/ProceduresScreen';
import { PeopleScreen } from './components/screens/PeopleScreen';
import { RecordsScreen } from './components/screens/RecordsScreen';
import { RecordTypesScreen } from './components/screens/RecordTypesScreen';
import { RolesScreen } from './components/screens/RolesScreen';
import { SuppliersScreen } from './components/screens/SuppliersScreen';

/** The short URL printed on the QR label. Kept short because it has to survive
    being encoded at 50 × 25 mm and still scan from a metre away. */
function AssetRedirect() {
  const { tag } = useParams();
  return <Navigate to={`/devices/${tag ?? ''}`} replace />;
}

export const router = createBrowserRouter([
  /* Add a joiner is a full page of its own, so it sits outside the sidebar shell. */
  { path: '/joiner', element: <JoinerWizard /> },
  { path: '/joiner/:id', element: <JoinerWizard /> },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <DashboardScreen /> },
      { path: 'expiries', element: <ExpiriesScreen /> },
      { path: 'locations', element: <LocationsScreen /> },
      { path: 'pack', element: <PackScreen /> },
      { path: 'labels', element: <LabelsScreen /> },
      { path: 'people', element: <PeopleScreen /> },
      { path: 'groups', element: <GroupsScreen /> },
      { path: 'devices', element: <DevicesScreen /> },
      /* The QR label target. Encoded on the label as /a/{assetTag} on whatever
         origin printed it — blueprint.hird.co.uk today — and redirected here
         (§12). Written as a fixed domain here until 2026-10-12, naming one
         that was never created; see LabelsScreen for why the label reads the
         origin rather than any note. */
      { path: 'devices/:tag', element: <DevicesScreen /> },
      { path: 'infrastructure', element: <InfrastructureScreen /> },
      { path: 'printers', element: <PrintersScreen /> },
      { path: 'a/:tag', element: <AssetRedirect /> },
      { path: 'import', element: <ImportScreen /> },

      /* Step 8 — the registers the expiry aggregation reads from. */
      { path: 'licences', element: <LicencesScreen /> },
      { path: 'network', element: <NetworkScreen /> },
      { path: 'applications', element: <ApplicationsScreen /> },
      { path: 'suppliers', element: <SuppliersScreen /> },
      { path: 'domains', element: <DomainsScreen /> },

      /* Step 9 — the record types engine. The :id form is where the expiry
         aggregation lands, so a custom expiry opens the record it came from. */
      { path: 'record-types', element: <RecordTypesScreen /> },
      { path: 'records/:typeId', element: <RecordsScreen /> },
      { path: 'records/:typeId/:id', element: <RecordsScreen /> },

      /* Steps 10 and 11. Knowledge base and Procedures are the two mechanisms
         the README names as what fights unmaintained content. */
      { path: 'knowledge-base', element: <KnowledgeBaseScreen /> },
      /* Keyed, so moving between the two does not carry one's filter into the other:
         they are the same component and React would otherwise reuse it. */
      { path: 'onboarding', element: <OnboardingScreen key="on" mode="onboarding" /> },
      { path: 'offboarding', element: <OffboardingScreen key="off" /> },
      { path: 'procedures', element: <ProceduresScreen /> },
      { path: 'backup-dr', element: <BackupDrScreen /> },
      { path: 'audit', element: <AuditScreen /> },
      { path: 'modules', element: <ModulesScreen /> },
      { path: 'notifications', element: <NotificationsScreen /> },
      { path: 'roles', element: <RolesScreen /> },
      { path: 'credentials', element: <CredentialsScreen /> },

      /* No placeholders left. The last one promised an API and webhooks, and
         nothing at Hird would have consumed either — a screen advertising work
         nobody asked for is a promise the register has to keep answering for. */

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
